import { useCallback } from 'react'

import { FormattedMessage } from 'react-intl'

import type { Budget as BudgetType } from '@subaio/api-types/budget'
import { useAsyncResource } from '@subaio/components'
import { delay } from '@subaio/utils'

import type { Step, StepType } from 'containers/Flow/Step'

import { IconWarning } from 'components/Icons'
import { InfoBox } from 'components/InfoBox'
import { Spinner } from 'components/Spinner'

import { DefaultErrorSteps } from '../DefaultErrorSteps'
import { Card } from './Card'
import { Container } from './Container'
import { ErrorWrapper } from './ErrorWrapper'
import { HeaderIconWrapper } from './HeaderIconWrapper'

type Props = TaggedType<Step, StepType.BudgetPoll> & {
	onGetBudget: () => Promise<BudgetType | undefined>
	onBudgetReady: () => Promise<void>
}

export const BudgetPoll = ({ onGetBudget, onBudgetReady }: Props) => {
	const budgetResource = useAsyncResource(
		useCallback(
			async (abortIfCancelled) => {
				let budget: BudgetType | undefined
				// We wait for the budget to be available. Will return an empty response with status code 202 while there is no bank data
				do {
					abortIfCancelled()
					budget = await onGetBudget()
					abortIfCancelled()
					// The budget-api can get overloaded if called too quickly
					if (!budget) await delay(7)
					abortIfCancelled()
				} while (!budget)

				await onBudgetReady()

				return budget
			},
			[onGetBudget, onBudgetReady],
		),
	)

	return (
		<Container>
			<Card>
				{budgetResource.status === 'loading' && !budgetResource.value && (
					<HeaderIconWrapper>
						<h3>
							<FormattedMessage
								id='flow.budgetEditForm.loading'
								defaultMessage='We are collecting your data - please wait'
							/>
						</h3>
						<Spinner />
					</HeaderIconWrapper>
				)}
				{budgetResource.status === 'errored' && (
					<ErrorWrapper>
						<InfoBox type='danger'>
							<div>
								<IconWarning />
								<h2>
									<FormattedMessage
										id='flow.budgetEditForm.loadErrorTitle'
										defaultMessage='Failed to generate your budget'
									/>
								</h2>
							</div>
							<div>
								<DefaultErrorSteps />
							</div>
						</InfoBox>
					</ErrorWrapper>
				)}
			</Card>
		</Container>
	)
}
